import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { getCapabilities } from "@/api/endpoints/organization";
import { getTags } from "@/api/endpoints/tags";
import { Language, MENTION_SOURCE_TYPE, Source } from "@/constants";
import { Organization } from "@/types/Organization";
import { UnsavedQuery } from "@/types/SearchQuery";
import { Tag } from "@/types/Tag";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  getMinReachByChannelTypeDefaultValues,
  MinReachByChannelTypeSource,
  MIN_REACH_BY_CHANNEL_TYPE_SOURCES,
} from "./partials/reuestUtils";
import { CandidateSearch } from "@/types/Search";
import { useSessionStorage } from "@/hooks/useSessionStorage";
import { useGroupedSearches } from "@/hooks/useGroupedSearches";
import { useHasFeature } from "@/hooks/useHasFeature";
import { FEATURE } from "@/features";
import { useOrganization } from "@/hooks/useOrganization";
import { PreviewResponse, previewSearch } from "@/api/endpoints/preview";
import { deleteNil } from "@/utils";
import { useSearches } from "@/hooks/useSearches";

type SearchContextType = {
  candidateSearch: CandidateSearch | undefined;
  setCandidateSearch: (value: CandidateSearch | undefined) => void;
  removeCandidateSearch: () => void;
  defaultQuery: UnsavedQuery;
  tags: Tag[];
  languages: Language[];
  sources: Source[];
  audioSources: Source[];
  newsSources: Source[];
  socialSources: Source[];
  industries: string[];
  minReachByChannelTypeSources: MinReachByChannelTypeSource[];
  isLoading: boolean;
  previewData: PreviewResponse | undefined;
  previewError: Error | null;
  page: number;
  setPage: (value: number) => void;
  hasMediaMonitoringProduct?: boolean;
  canCreateActiveSearch?: boolean;
  activeSearchesCount?: number;
  maxNumAlertsCount?: number;
};

const SearchContext = createContext<SearchContextType>({
  candidateSearch: undefined,
  setCandidateSearch: () => undefined,
  removeCandidateSearch: () => undefined,
  defaultQuery: {} as UnsavedQuery,
  tags: [],
  languages: [],
  sources: [],
  audioSources: [],
  newsSources: [],
  socialSources: [],
  industries: [],
  minReachByChannelTypeSources: [],
  isLoading: false,
  previewData: undefined,
  previewError: null,
  page: 1,
  setPage: () => undefined,
  hasMediaMonitoringProduct: undefined,
  canCreateActiveSearch: undefined,
  activeSearchesCount: undefined,
  maxNumAlertsCount: undefined,
});

export function SearchProvider({ children }: { children: ReactNode }) {
  // Local state
  const [candidateSearch, setCandidateSearch, removeCandidateSearch] =
    useSessionStorage<CandidateSearch | undefined>(
      "candidate-search",
      undefined
    );
  const [page, setPage] = useState<number>(1);

  // URL state
  const { orgUuid } = useParams<{ orgUuid: Organization["uuid"] }>();

  // Server state
  const { data: organization } = useOrganization();

  const plan = organization?.ok ? organization.data.plan : null;

  const hasMediaMonitoringProduct = useHasFeature(
    FEATURE.HAS_MEDIA_MONITORING_PRODUCT
  );

  const { data: searches } = useSearches(orgUuid);

  const { activeSearches } = useGroupedSearches(searches);

  const {
    error: previewError,
    data: _previewData,
    isLoading,
  } = useQuery({
    queryKey: [candidateSearch, "preview", page],
    refetchOnWindowFocus: false,
    queryFn: () => {
      if (!candidateSearch) {
        return;
      }

      return previewSearch({
        end_date: candidateSearch.end_date || null,
        start_date: candidateSearch.start_date || null,
        queries: candidateSearch.queries.map((q) => deleteNil(q)),
        industry: null,
        page_size: 20,
        page_token: (page - 1) * 20,
      });
    },
    enabled: Boolean(candidateSearch),
  });

  const previewData = _previewData?.ok ? _previewData.data : undefined;

  const { data: capabilitiesData } = useQuery({
    queryFn: () => getCapabilities(orgUuid),
    queryKey: [orgUuid, "capabilities"],
    enabled: Boolean(orgUuid),
  });

  const { data: tagsData } = useQuery({
    queryKey: [orgUuid, "tags"],
    queryFn: () => getTags(),
  });

  // Helpers
  const capabilities = capabilitiesData?.ok ? capabilitiesData.data : undefined;

  const tags = tagsData?.ok
    ? tagsData.data.filter((tag) => tag.slug !== "favorited")
    : [];

  const {
    languages,
    sources,
    audioSources,
    newsSources,
    socialSources,
    industries,
    minReachByChannelTypeSources,
  } = useMemo(() => {
    const languages = capabilities?.languages || [];
    const sources = capabilities?.sources || [];
    const audioSources = capabilities?.sources_audio || [];
    const newsSources = capabilities?.sources_news || [];
    const socialSources = capabilities?.sources_social || [];
    const industries = capabilities?.industries || [];
    const uniqueIndustries = [...new Set(industries)];
    const minReachByChannelTypeSources =
      MIN_REACH_BY_CHANNEL_TYPE_SOURCES.filter((source) =>
        sources.includes(source)
      );

    return {
      languages,
      sources,
      audioSources,
      newsSources,
      socialSources,
      minReachByChannelTypeSources,
      industries: uniqueIndustries,
    };
  }, [capabilities]);

  const defaultQuery: UnsavedQuery = useMemo(() => {
    return {
      keywords: [],
      near_keywords: [],
      not_near_keywords: [],
      not_near_distance: 10,
      near_distance: 10,
      languages,
      sources,
      mention_types: MENTION_SOURCE_TYPE.ALL,
      min_reach_by_channel_type: getMinReachByChannelTypeDefaultValues(
        minReachByChannelTypeSources
      ),
      tags: null,
      include_channel_names: null,
      exclude_channel_names: null,
      include_paywalled: null,
      near_near_keywords: null,
    };
  }, [languages, minReachByChannelTypeSources, sources]);

  const maxNumAlertsCount = plan?.max_num_reports;
  const activeSearchesCount = activeSearches.length;
  const canCreateActiveSearch = activeSearchesCount < (maxNumAlertsCount ?? 0);

  return (
    <SearchContext.Provider
      value={{
        candidateSearch,
        setCandidateSearch,
        removeCandidateSearch,
        defaultQuery,
        tags,
        audioSources,
        newsSources,
        socialSources,
        industries,
        minReachByChannelTypeSources,
        languages,
        sources,
        isLoading,
        previewData,
        previewError,
        page,
        setPage,
        canCreateActiveSearch,
        maxNumAlertsCount,
        activeSearchesCount,
        hasMediaMonitoringProduct,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearchContext() {
  return useContext(SearchContext);
}
